import React, { useState } from 'react';
import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Button from '@material-ui/core/Button';
import IconButton from '@material-ui/core/IconButton';
import Typography from '@material-ui/core/Typography';
import Box from '@material-ui/core/Box';
import { makeStyles } from '@material-ui/core/styles';
import ClearIcon from '@material-ui/icons/Clear';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import moment from 'moment';
import numeral from 'numeral';
import clsx from 'clsx';
import { LongText, StatusChip } from 'src/components';
import { Tooltip } from '@material-ui/core';

const useStyles = makeStyles(theme => ({
  wrapTableCell: {
    whiteSpace: 'nowrap',
    wordBreak: 'break-word', // break long words
    maxWidth: 500
  },
  particulars: {
    whiteSpace: 'normal',
    wordBreak: 'break-word'
  },
  center: {
    textAlign: 'center'
  },
  right: {
    textAlign: 'right'
  },
  minWidth: {
    minWidth: 100
  },
  dialogPaper: {
    [theme.breakpoints.down('xl')]: {
      margin: 0
    },
    [theme.breakpoints.down('lg')]: {
      margin: 0
    },
    [theme.breakpoints.down('md')]: {
      margin: 0
    },
    [theme.breakpoints.down('sm')]: {
      width: '100%',
      height: '100%',
      margin: 0,
      maxWidth: '100%',
      maxHeight: 'none',
      borderRadius: 0
    },
    [theme.breakpoints.down('xs')]: {
      width: '100%',
      height: '100%',
      margin: 0,
      maxWidth: '100%',
      maxHeight: 'none',
      borderRadius: 0
    }
  },
  dialogContent: {
    padding: 0,
    margin: 0
  }
}));

export const Remarks = ({ data }) => {
  const [open, setOpen] = useState(false);

  if (!open) {
    return (
      <Button color="primary" onClick={() => setOpen(prev => !prev)}>
        Show Remarks
      </Button>
    );
  }

  return (
    <Box>
      <Box mb={2}> {data}</Box>
      <Button color="primary" onClick={() => setOpen(prev => !prev)}>
        Hide Remarks
      </Button>
    </Box>
  );
};

export const ReqBalDetailsDialog = ({ open, handleClose, children }) => {
  const classes = useStyles();
  return (
    <Dialog
      fullWidth
      maxWidth="lg"
      open={open}
      onClose={handleClose}
      classes={{ paper: classes.dialogPaper }}
    >
      <DialogTitle disableTypography>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h4" color="textSecondary">
            Require Balance Details
          </Typography>
          <IconButton onClick={handleClose}>
            <ClearIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent className={classes.dialogContent}>
        {children}
      </DialogContent>
    </Dialog>
  );
};

export const Item = ({ data, remarks }) => {
  const classes = useStyles();

  const showTransaction = () => {
    window.open('/app/trans/' + data.jid, '_blank');
  };

  return (
    <TableRow>
      <TableCell className={classes.wrapTableCell}>
        <strong>{data.RefNo}</strong> <br />
        {moment(data.jDate).format('MM/DD/YYYY')}
      </TableCell>
      <TableCell className={classes.wrapTableCell}>{data.JCd}</TableCell>

      <TableCell className={classes.wrapTableCell}>
        <strong>{data.Name}</strong> <br />
        <div className={classes.particulars}>{data.Particulars}</div>
      </TableCell>

      <TableCell className={classes.wrapTableCell}>
        <StatusChip status={data.jStatus} size="small" />
      </TableCell>

      <TableCell className={clsx(classes.wrapTableCell, classes.right)}>
        {numeral(data.dr).format('0,0.00')}
      </TableCell>
      <TableCell className={clsx(classes.wrapTableCell, classes.right)}>
        {numeral(data.cr).format('0,0.00')}
      </TableCell>
      <TableCell className={clsx(classes.wrapTableCell, classes.right)}>
        <strong> {numeral(data.bal).format('0,0.00')}</strong>
      </TableCell>
      <TableCell>
        <LongText text={data.Remarks} disableTypography={true} />
      </TableCell>
      <TableCell>
        <LongText text={data.jeRemarks} disableTypography={true} />
      </TableCell>
      <TableCell align="right">
        <Tooltip title="Open in New Tab">
          <IconButton onClick={showTransaction} color="primary">
            <OpenInNewIcon />
          </IconButton>
        </Tooltip>
      </TableCell>
    </TableRow>
  );
};

export const DetailsTable = ({ items }) => {
  const classes = useStyles();
  // only fa and approved are counted towards the total
  const filteredItems = (items ?? []).filter(
    item => item.jStatus == 1 || item.jStatus == 0
  );
  const total = filteredItems.reduce(
    (acc, prev) => {
      acc.dr = acc.dr + (prev.dr ?? 0);
      acc.cr = acc.cr + (prev.cr ?? 0);
      return acc;
    },
    {
      dr: 0,
      cr: 0
    }
  );
  return (
    <TableContainer>
      <Table stickyHeader aria-label="sticky table">
        <TableHead>
          <TableRow>
            <TableCell className={clsx(classes.left, classes.wrapTableCell)}>
              Ref No.
            </TableCell>
            <TableCell className={clsx(classes.left, classes.wrapTableCell)}>
              Type
            </TableCell>
            <TableCell className={clsx(classes.left, classes.wrapTableCell)}>
              Name
            </TableCell>
            <TableCell className={clsx(classes.left, classes.wrapTableCell)}>
              Status
            </TableCell>
            <TableCell className={clsx(classes.center, classes.wrapTableCell)}>
              Debit
            </TableCell>
            <TableCell className={clsx(classes.center, classes.wrapTableCell)}>
              Credit
            </TableCell>
            <TableCell className={clsx(classes.center, classes.wrapTableCell)}>
              Balance
            </TableCell>
            <TableCell className={classes.center}>Remarks</TableCell>
            <TableCell className={classes.center}>JE Remarks</TableCell>

            <TableCell />
          </TableRow>
        </TableHead>
        <TableBody>
          {items.map((data, i) => (
            <Item data={data} key={i} />
          ))}

          {items.length === 0 && (
            <TableRow>
              <TableCell colSpan="100%" align="center">
                No data to show
              </TableCell>
            </TableRow>
          )}

          {items.length > 0 && (
            <TableRow>
              <TableCell colSpan={4} align="right">
                <strong>Total : </strong>
              </TableCell>

              <TableCell align="right">
                <strong> {numeral(total.dr).format('0,0.00')}</strong>
              </TableCell>
              <TableCell align="right">
                <strong> {numeral(total.cr).format('0,0.00')}</strong>
              </TableCell>
              <TableCell align="right">
                {
                  <strong>
                    {numeral(
                      filteredItems &&
                        filteredItems.length > 0 &&
                        filteredItems[filteredItems.length - 1]?.bal != null
                        ? filteredItems[filteredItems.length - 1].bal
                        : 0
                    ).format('0,0.00')}
                  </strong>
                }
              </TableCell>
              <TableCell colSpan={2} />
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};
